import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { InteractionService, IInteraction } from '../../../core/services/interaction.service';
import { UserService, User } from '../../../core/services/user.service';

@Component({
  selector: 'app-interaction-list',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    TableModule,
    ButtonModule,
    TagModule,
    InputTextModule,
    SelectModule,
    FormsModule,
    ProgressSpinnerModule,
    TooltipModule
  ],
  templateUrl: './interaction-list.html',
  styleUrl: './interaction-list.scss'
})
export class InteractionList implements OnInit {
  interactions: IInteraction[] = [];
  filteredInteractions: IInteraction[] = [];
  currentUser: User | null = null;
  loading = true;
  loadingError: string | null = null;
  searchTerm = '';
  typeFilter = '';
  statusFilter = '';

  typeOptions = [
    { label: 'All', value: '' },
    { label: 'Sales Call', value: 'Sales Call' },
    { label: 'Demo', value: 'Demo' },
    { label: 'Service Call', value: 'Service Call' },
    { label: 'Follow-up Visit', value: 'Follow-up Visit' },
    { label: 'Emergency Call', value: 'Emergency Call' }
  ];

  statusOptions = [
    { label: 'All', value: '' },
    { label: 'Scheduled', value: 'Scheduled' }, 
    { label: 'Completed', value: 'Completed' },
  ];

  constructor(
    public router: Router,
    private interactionService: InteractionService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.loadCurrentUser();
  }

  loadCurrentUser() {
    this.loading = true; // Ensure loading starts when fetching user
    this.userService.getCurrentUserProfile().subscribe({
      next: (user) => {
        this.currentUser = user;
        if (user._id) {
          this.loadInteractions(user._id);
        } else {
          console.error('No user ID available');
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Error loading current user:', error);
        this.loadingError = 'Failed to load user profile. Please try refreshing the page.';
        this.loading = false;
      }
    });
  }

  loadInteractions(userId?: string) {
    this.loading = true;
    const targetUserId = userId || this.currentUser?._id;
    
    if (!targetUserId) {
      console.error('No user ID available for loading interactions');
      this.loading = false;
      return;
    }

    this.interactionService.getInteractionsByUserId(targetUserId).subscribe({
      next: (interactions) => {
        this.interactions = interactions;
        this.applyFilters();
        this.loading = false;
        console.log('Loaded interactions:', interactions);
      },
      error: (error) => {
        console.error('Error loading interactions:', error);
        this.interactions = [];
        this.filteredInteractions = [];
        this.loadingError = 'Failed to load interactions. Please try refreshing the page.';
        this.loading = false;
      }
    });
  }

  getTypeSeverity(type: string): string {
    switch (type) {
      case 'Sales Call':
        return 'info';
      case 'Demo':
        return 'warning';
      case 'Service Call':
        return 'success';
      case 'Follow-up Visit':
        return 'secondary';
      case 'Emergency Call':
        return 'danger';
      default:
        return 'info';
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Scheduled':
        return 'info';
      case 'In Progress':
        return 'warning';
      case 'Cancelled':
        return 'danger';
      default:
        return 'info';
    }
  }

  onViewInteraction(interactionId: string) {
    this.router.navigate(['/admin/log-interaction/update', interactionId]);
  }

  onViewReservation(interaction: IInteraction) {
    const reservationId = this.getReservationId(interaction);
    if (reservationId) {
      this.router.navigate(['/admin/view-reservation', reservationId]);
    }
  }

  onViewCustomer(interaction: IInteraction) {
    const customerId = this.getCustomerId(interaction);
    if (customerId) {
      this.router.navigate(['/admin/view-customer', customerId]);
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  // Filter functionality
  applyFilters() {
    let filtered = [...this.interactions];

    // Search filter
    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(interaction => {
        const customerName = this.getCustomerName(interaction);
        return customerName.toLowerCase().includes(searchLower) ||
               interaction.interactionType.toLowerCase().includes(searchLower) ||
               interaction.interactionStatus.toLowerCase().includes(searchLower) ||
               (interaction.interactionNotes && interaction.interactionNotes.toLowerCase().includes(searchLower));
      });
    }

    // Type filter
    if (this.typeFilter) {
      filtered = filtered.filter(interaction => 
        this.mapInteractionType(interaction.interactionType) === this.typeFilter
      );
    }

    // Status filter
    if (this.statusFilter) {
      filtered = filtered.filter(interaction => 
        this.mapInteractionStatus(interaction.interactionStatus) === this.statusFilter
      );
    }

    this.filteredInteractions = filtered;
  }

  onSearchChange() {
    this.applyFilters();
  }

  onTypeFilterChange() {
    this.applyFilters();
  }

  onStatusFilterChange() {
    this.applyFilters();
  }

  // Helper methods for data mapping
  getCustomerName(interaction: IInteraction): string {
    if (typeof interaction.reservationId === 'object' && interaction.reservationId?.customerId) {
      return interaction.reservationId.customerId.businessName || 'Unknown Customer';
    }
    return 'Unknown Customer';
  }

  getCustomerId(interaction: IInteraction): string {
    if (typeof interaction.reservationId === 'object' && interaction.reservationId?.customerId) {
      return interaction.reservationId.customerId._id || '';
    }
    return '';
  }

  getCustomerLocation(interaction: IInteraction): string {
    if (typeof interaction.reservationId === 'object' && interaction.reservationId?.customerId) {
      const address = interaction.reservationId.customerId.address;
      return address ? `${address.city}, ${address.state}` : 'Location not available';
    }
    return 'Location not available';
  }

  getReservationId(interaction: IInteraction): string {
    if (typeof interaction.reservationId === 'string') {
      return interaction.reservationId;
    }
    return interaction.reservationId?._id || '';
  }

  getReservationStatus(interaction: IInteraction): string {
    if (typeof interaction.reservationId === 'object') {
      return interaction.reservationId?.status || 'Unknown';
    }
    return 'Unknown';
  }

  mapInteractionType(type: string): string {
    const typeMapping: { [key: string]: string } = {
      'sales_call': 'Sales Call',
      'demo': 'Demo',
      'service_call': 'Service Call',
      'follow_up': 'Follow-up Visit',
      'emergency': 'Emergency Call'
    };
    return typeMapping[type] || type;
  }

  mapInteractionStatus(status: string): string {
    const statusMapping: { [key: string]: string } = {
      'upcoming': 'Scheduled',
      'completed': 'Completed',
      'in_progress': 'In Progress',
      'cancelled': 'Cancelled'
    };
    return statusMapping[status] || status;
  }
}




